import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Input, Text } from '@/ui-kit'
import { SCREEN_SIZE, useMediaQuery } from '@/ui-kit/media'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { useMemo, useRef, useState } from 'react'
import type { FormSelectOption } from './FormSelect'
import { FormSelectMobileDrawer } from './FormSelectMobileDrawer'

export type { FormSelectOption }

type FormSelectDropdownProps = {
  value: string | undefined
  valueLabel?: string | undefined
  onChange: (value: string) => void
  label: string
  options: readonly FormSelectOption[]
  placeholder: string
  errorMessage?: string
  searchable?: boolean
}

const SEARCH_MIN_OPTIONS = 10
const VISIBLE_OPTIONS_LIMIT = 250

const splitWords = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .split(/[\s\-_/(),.]+/)
    .filter(Boolean)

const matchesByWordPrefix = (label: string, query: string) => {
  const queryWords = splitWords(query)
  if (!queryWords.length) return true

  const labelWords = splitWords(label)
  return queryWords.every((queryWord) => labelWords.some((labelWord) => labelWord.startsWith(queryWord)))
}

export const FormSelectDropdown = ({
  value,
  valueLabel: valueLabelProp,
  onChange,
  label,
  options,
  placeholder,
  errorMessage,
  searchable = false
}: FormSelectDropdownProps) => {
  const { t } = useLingui()
  const isError = !!errorMessage
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const isMobile = useMediaQuery(SCREEN_SIZE.MOBILE)

  const valueLabel = useMemo(() => {
    return valueLabelProp || options.find((opt) => opt.value === value)?.label || placeholder
  }, [options, value, placeholder, valueLabelProp])

  const { filteredOptions, hasMoreMatches } = useMemo(() => {
    if (!searchable) return { filteredOptions: options, hasMoreMatches: false }
    if (!searchQuery.trim()) {
      const list = options.slice(0, VISIBLE_OPTIONS_LIMIT)
      return { filteredOptions: list, hasMoreMatches: options.length > VISIBLE_OPTIONS_LIMIT }
    }
    const filtered = options.filter((opt) => matchesByWordPrefix(opt.label, searchQuery))
    const visible = filtered.slice(0, VISIBLE_OPTIONS_LIMIT)
    return { filteredOptions: visible, hasMoreMatches: filtered.length > VISIBLE_OPTIONS_LIMIT }
  }, [options, searchable, searchQuery])

  if (isMobile) {
    return (
      <FormSelectMobileDrawer
        value={value}
        valueLabel={valueLabelProp}
        onChange={onChange}
        label={label}
        options={options}
        placeholder={placeholder}
        errorMessage={errorMessage}
        searchable={searchable}
      />
    )
  }

  const showSearch = searchable && options.length >= (isMobile ? 5 : SEARCH_MIN_OPTIONS)

  const focusSearchInputDeferred = () => {
    const run = () => searchInputRef.current?.focus({ preventScroll: true })
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        run()
        setTimeout(run, 0)
      })
    })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchQuery('')
      return
    }
    if (showSearch) {
      focusSearchInputDeferred()
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <DropdownMenu modal={true} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger
          label={label}
          className={clsx('flex h-[56px] w-full flex-col items-start gap-0!', { 'border-red-500': isError })}
        >
          <span className={clsx('line-clamp-1 text-left', !value && 'text-gray-80')}>{valueLabel}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="start"
          className={clsx('flex max-h-96 w-(--radix-dropdown-menu-trigger-width) flex-col overflow-hidden p-0', {
            'pt-2': showSearch
          })}
        >
          {showSearch && (
            <div className="backgroundColorBlackToWhite sticky top-0 z-10 border-b border-solid border-gray-100 p-1">
              <Input
                ref={searchInputRef}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t`Search...`}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') return
                  e.stopPropagation()
                }}
              />
            </div>
          )}
          <div
            className="scrollbar min-h-0 flex-1 overflow-y-auto p-1"
            onPointerMoveCapture={(e) => {
              if (searchInputRef.current && document.activeElement === searchInputRef.current) {
                e.stopPropagation()
              }
            }}
          >
            {filteredOptions.map((opt) => (
              <DropdownMenuItem
                key={opt.value}
                textValue={opt.label}
                className={clsx(opt.value === value && 'bg-gray-100/60')}
                onSelect={() => {
                  onChange(opt.value)
                }}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </div>
          {showSearch && hasMoreMatches && (
            <div className="text-gray-80 text-body3-regular border-t border-solid border-gray-100 px-2 py-2 text-center">
              <Trans>Refine your query to see more options</Trans>
            </div>
          )}
          {showSearch && filteredOptions.length === 0 && (
            <div className="text-gray-80 text-body3-regular py-4 text-center">
              <Trans>No matches</Trans>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {errorMessage && (
        <Text type="body3-regular" className="text-red-500">
          {errorMessage}
        </Text>
      )}
    </div>
  )
}
